import { useContext } from "react";
import { useTranslation } from "react-i18next";
import { CardNotification } from "@foris/avocado-suite";
import type { Classroom, Instructor } from "@models/ISchema";
import type { SessionAssignationRowPatch } from "@services/types/session-assignation";
import { ContextApp } from "@config/Context/contextApp";
import type {
  SessionAssignationActionResult,
  SessionAssignationResourceTarget,
} from "../../types/session-assignation";
import { useSessionResourceDraft } from "../../hooks/session-assignation/useSessionResourceDraft";
import useClassroomSearch from "../../hooks/useClassroomSearch";
import useInstructorSearch from "../../hooks/useInstructorSearch";
import ClassroomPicker from "../classroom-picker";
import InstructorPicker from "../instructor-picker";
import AssignmentEditorDrawer from "../assignment-editor-drawer";

interface SessionResourceDrawerProps {
  target: SessionAssignationResourceTarget | null;
  isOpen: boolean;
  courseId?: string;
  disabled: boolean;
  onDirty: (dirty: boolean) => void;
  onApply: (patch: SessionAssignationRowPatch) => SessionAssignationActionResult;
  onRequestClose: () => void;
  onClose: () => void;
}

const SessionResourceDrawer = ({
  target,
  isOpen,
  courseId,
  disabled,
  onDirty,
  onApply,
  onRequestClose,
  onClose,
}: SessionResourceDrawerProps) => {
  const { t } = useTranslation();
  const { user } = useContext(ContextApp);
  const { selected, shouldFilter, setShouldFilter, select, remove, issue, setIssue } =
    useSessionResourceDraft(target, onDirty, isOpen);
  const isClassroom = target?.field === "classrooms";
  const classrooms = useClassroomSearch(isOpen && isClassroom);
  const instructors = useInstructorSearch({
    courseId,
    shouldFilter,
    enabled: isOpen && !isClassroom && !!courseId,
  });
  return (
    <AssignmentEditorDrawer
      isOpen={isOpen}
      title={t(
        `assignation-edit.session-view.resources.${isClassroom ? "classroom" : "instructor"}`,
      )}
      onClose={onClose}
      onRequestClose={onRequestClose}
      disabled={disabled}
      onApply={() => {
        if (!isOpen || !target || (!isClassroom && !courseId)) return;
        const result = onApply(
          isClassroom
            ? { classrooms: selected as Classroom[] }
            : { instructors: selected as Instructor[] },
        );
        if (result.ok === false) setIssue(result.code);
      }}
    >
      {issue && (
        <div role="alert">
          <CardNotification
            state="error"
            title={t(`assignation-edit.session-view.errors.${issue}`, {
              defaultValue: t("assignation-edit.session-view.errors.ACTION_FAILED"),
            })}
          />
        </div>
      )}
      {isClassroom ? (
        <ClassroomPicker
          classrooms={selected as Classroom[]}
          disabled={disabled}
          {...classrooms}
          onSelect={select}
          onDelete={remove}
        />
      ) : (
        <InstructorPicker
          instructors={selected as Instructor[]}
          disabled={disabled}
          {...instructors}
          shouldFilterInstructors={shouldFilter}
          canAssignFromOtherCourses={!!user?.abilities?.can_assign_instructors_from_other_courses}
          onFilterChange={setShouldFilter}
          onSelect={select}
          onDelete={remove}
        />
      )}
    </AssignmentEditorDrawer>
  );
};

export default SessionResourceDrawer;
